import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit,
  Timestamp
} from 'firebase/firestore';
import { db, collections } from '../../firebase.marketplace';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Divider, 
  Grid, 
  Card, 
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  MonetizationOn as MoneyIcon,
  ShoppingBag as ProductIcon,
  TrendingUp as TrendingIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as ChartTooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

// Styled components
const Container = styled(Box)`
  min-height: 100vh;
  background: linear-gradient(180deg, #030617 0%, #0a1128 100%);
  padding: 2rem 1rem;
`;

const DashboardContainer = styled(Paper)`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 10px;
  padding: 2rem;
  color: white;
`;

const DashboardHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const StatsGrid = styled(Grid)`
  margin-bottom: 2rem;
`;

const StatCard = styled(Card)`
  height: 100%;
  background: rgba(255, 255, 255, 0.08);
  color: white;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const StatCardContent = styled(CardContent)`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StatIcon = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${props => props.bgcolor || '#4a90e2'};
  margin-bottom: 1rem;
`;

const StatValue = styled(Typography)`
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled(Typography)`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
`;

const ChartContainer = styled(Box)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  height: 300px;
`;

const StyledTableContainer = styled(TableContainer)`
  margin-top: 2rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  
  .MuiTableCell-root {
    color: white;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .MuiTableCell-head {
    background: rgba(0, 0, 0, 0.2);
    font-weight: bold;
  }
`;

// Platform fee percentage
const PLATFORM_FEE_PERCENTAGE = 5;

// Chart colors
const CHART_COLORS = ['#4a90e2', '#ff5722', '#4caf50', '#9c27b0', '#ff9800'];

const ArtistEarningsDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({
    totalEarnings: 0,
    totalSales: 0,
    totalProducts: 0,
    platformFees: 0
  });
  const [tabValue, setTabValue] = useState(0);
  const [timeRange, setTimeRange] = useState('all'); // 'all', 'month', 'week'
  const [salesData, setSalesData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  
  useEffect(() => {
    // Check if user is logged in and is an artist
    if (!currentUser) {
      navigate('/marketplace/auth/signin');
      return;
    }
    
    fetchData();
  }, [currentUser, navigate, timeRange]);
  
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch products - simplified query to avoid index errors
      const productsQuery = query(
        collection(db, collections.PRODUCTS),
        where('sellerId', '==', currentUser.uid)
      );
      const productsSnapshot = await getDocs(productsQuery);
      const productsData = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort in memory instead of using orderBy to avoid index requirements
      productsData.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date();
        const dateB = b.createdAt?.toDate?.() || new Date();
        return dateB - dateA; // descending order
      });
      
      setProducts(productsData);
      
      // Fetch orders - simplified query to avoid index errors
      const ordersQuery = query(
        collection(db, collections.ORDERS),
        where('items.sellerId', '==', currentUser.uid)
      );
      const ordersSnapshot = await getDocs(ordersQuery);
      let ordersData = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Apply time filtering in memory instead of in the query
      if (timeRange === 'week') {
        const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        ordersData = ordersData.filter(order => {
          const orderDate = order.createdAt?.toDate?.() || new Date();
          return orderDate >= oneWeekAgo;
        });
      } else if (timeRange === 'month') {
        const oneMonthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        ordersData = ordersData.filter(order => {
          const orderDate = order.createdAt?.toDate?.() || new Date();
          return orderDate >= oneMonthAgo;
        });
      }
      
      // Sort in memory
      ordersData.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date();
        const dateB = b.createdAt?.toDate?.() || new Date();
        return dateB - dateA; // descending order
      });
      
      setOrders(ordersData);
      
      // Calculate stats
      let totalEarnings = 0;
      let totalSales = 0;
      let platformFees = 0;
      
      // Process orders to calculate earnings
      ordersData.forEach(order => {
        order.items.forEach(item => {
          if (item.sellerId === currentUser.uid) {
            const itemTotal = item.price * item.quantity;
            const platformFee = (itemTotal * PLATFORM_FEE_PERCENTAGE) / 100;
            const artistEarnings = itemTotal - platformFee;
            
            totalEarnings += artistEarnings;
            totalSales += item.quantity;
            platformFees += platformFee;
          }
        });
      });
      
      setStats({
        totalEarnings,
        totalSales,
        totalProducts: productsData.length,
        platformFees
      });
      
      // Prepare chart data
      prepareSalesData(ordersData);
      prepareCategoryData(productsData);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const prepareSalesData = (ordersData) => {
    // Group sales by date
    const salesByDate = {};
    
    ordersData.forEach(order => {
      const date = new Date(order.createdAt?.toDate() || order.createdAt);
      const dateStr = date.toISOString().split('T')[0];
      
      if (!salesByDate[dateStr]) {
        salesByDate[dateStr] = {
          date: dateStr,
          sales: 0,
          earnings: 0
        };
      }
      
      order.items.forEach(item => {
        if (item.sellerId === currentUser.uid) {
          const itemTotal = item.price * item.quantity;
          const platformFee = (itemTotal * PLATFORM_FEE_PERCENTAGE) / 100;
          const artistEarnings = itemTotal - platformFee;
          
          salesByDate[dateStr].sales += item.quantity;
          salesByDate[dateStr].earnings += artistEarnings;
        }
      });
    });
    
    // Convert to array and sort by date
    const salesData = Object.values(salesByDate).sort((a, b) => a.date.localeCompare(b.date));
    
    // Limit to last 10 data points for better visualization
    setSalesData(salesData.slice(-10));
  };
  
  const prepareCategoryData = (productsData) => {
    // Group products by category
    const categoryCount = {};
    
    productsData.forEach(product => {
      const category = product.category || 'Uncategorized';
      
      if (!categoryCount[category]) {
        categoryCount[category] = 0;
      }
      
      categoryCount[category]++;
    });
    
    // Convert to array for chart
    const categoryData = Object.entries(categoryCount).map(([name, value]) => ({
      name,
      value
    }));
    
    setCategoryData(categoryData);
  };
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  
  // Instead of a full-page loading indicator, we'll show the dashboard with loading states
  // This provides a better user experience by showing the structure of the page while data loads
  
  return (
    <Container>
      <DashboardContainer elevation={3}>
        <DashboardHeader>
          <Box>
            <Typography variant="h4" gutterBottom>
              Artist Dashboard
            </Typography>
            <Typography variant="body1" color="rgba(255, 255, 255, 0.7)">
              Track your sales, earnings, and products
            </Typography>
            {loading && (
              <Box mt={1}>
                <Alert severity="info" icon={<CircularProgress size={20} color="info" />}>
                  Loading your dashboard data...
                </Alert>
              </Box>
            )}
          </Box>
          
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/marketplace/products/create')}
            >
              Add New Product
            </Button>
            
            <IconButton 
              color="primary" 
              onClick={() => fetchData()}
              title="Refresh data"
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </DashboardHeader>
        
        {error && (
          <Alert 
            severity="error" 
            style={{ marginBottom: '1.5rem', backgroundColor: 'rgba(211, 47, 47, 0.1)', color: '#f44336' }}
          >
            {error}
          </Alert>
        )}
        
        <StatsGrid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <StatCardContent>
                <StatIcon bgcolor="#4a90e2">
                  <MoneyIcon />
                </StatIcon>
                <StatValue>{formatCurrency(stats.totalEarnings)}</StatValue>
                <StatLabel>Total Earnings</StatLabel>
              </StatCardContent>
            </StatCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <StatCardContent>
                <StatIcon bgcolor="#4caf50">
                  <TrendingIcon />
                </StatIcon>
                <StatValue>{stats.totalSales}</StatValue>
                <StatLabel>Total Sales</StatLabel>
              </StatCardContent>
            </StatCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <StatCardContent>
                <StatIcon bgcolor="#ff9800">
                  <ProductIcon />
                </StatIcon>
                <StatValue>{stats.totalProducts}</StatValue>
                <StatLabel>Products Listed</StatLabel>
              </StatCardContent>
            </StatCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <StatCardContent>
                <StatIcon bgcolor="#f44336">
                  <MoneyIcon />
                </StatIcon>
                <StatValue>{formatCurrency(stats.platformFees)}</StatValue>
                <StatLabel>Platform Fees ({PLATFORM_FEE_PERCENTAGE}%)</StatLabel>
              </StatCardContent>
            </StatCard>
          </Grid>
        </StatsGrid>
        
        <Box mb={3} display="flex" justifyContent="flex-end">
          <Button 
            variant={timeRange === 'all' ? 'contained' : 'outlined'}
            color="primary"
            size="small"
            onClick={() => handleTimeRangeChange('all')}
            style={{ marginRight: '0.5rem' }}
          >
            All Time
          </Button>
          <Button 
            variant={timeRange === 'month' ? 'contained' : 'outlined'}
            color="primary"
            size="small"
            onClick={() => handleTimeRangeChange('month')}
            style={{ marginRight: '0.5rem' }}
          >
            This Month
          </Button>
          <Button 
            variant={timeRange === 'week' ? 'contained' : 'outlined'}
            color="primary"
            size="small"
            onClick={() => handleTimeRangeChange('week')}
          >
            This Week
          </Button>
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <ChartContainer>
              <Typography variant="h6" gutterBottom>
                Sales & Earnings Over Time
              </Typography>
              
              {salesData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={salesData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: 'rgba(255,255,255,0.7)' }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }}
                    />
                    <YAxis tick={{ fill: 'rgba(255,255,255,0.7)' }} />
                    <ChartTooltip 
                      formatter={(value, name) => {
                        return name === 'earnings' 
                          ? [formatCurrency(value), 'Earnings'] 
                          : [value, 'Sales'];
                      }}
                    />
                    <Bar dataKey="sales" fill="#4a90e2" name="Sales" />
                    <Bar dataKey="earnings" fill="#4caf50" name="Earnings" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="rgba(255,255,255,0.5)">
                    No sales data available for the selected time period
                  </Typography>
                </Box>
              )}
            </ChartContainer>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <ChartContainer>
              <Typography variant="h6" gutterBottom>
                Products by Category
              </Typography>
              
              {categoryData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <ChartTooltip formatter={(value, name) => [value, name]} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="rgba(255,255,255,0.5)">
                    No category data available
                  </Typography>
                </Box>
              )}
            </ChartContainer>
          </Grid>
        </Grid>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider', marginTop: 4 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="My Products" />
            <Tab label="Recent Orders" />
          </Tabs>
        </Box>
        
        {tabValue === 0 && (
          <StyledTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Sales</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.length > 0 ? (
                  products.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {product.imageUrl && (
                            <Box
                              component="img"
                              src={product.imageUrl}
                              alt={product.name}
                              sx={{ width: 40, height: 40, borderRadius: 1, marginRight: 2, objectFit: 'cover' }}
                            />
                          )}
                          {product.name}
                        </Box>
                      </TableCell>
                      <TableCell>{product.category}</TableCell>
                      <TableCell align="right">{formatCurrency(product.price)}</TableCell>
                      <TableCell align="right">{product.sales || 0}</TableCell>
                      <TableCell align="right">{formatCurrency((product.sales || 0) * product.price)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Product">
                          <IconButton 
                            size="small" 
                            onClick={() => navigate(`/marketplace/products/${product.id}`)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Product">
                          <IconButton 
                            size="small" 
                            onClick={() => navigate(`/marketplace/products/edit/${product.id}`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No products found. Create your first product to start selling!
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        )}
        
        {tabValue === 1 && (
          <StyledTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Your Earnings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.length > 0 ? (
                  orders.map((order) => {
                    // Calculate artist's items and earnings for this order
                    const artistItems = order.items.filter(item => item.sellerId === currentUser.uid);
                    const orderTotal = artistItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
                    const platformFee = (orderTotal * PLATFORM_FEE_PERCENTAGE) / 100;
                    const artistEarnings = orderTotal - platformFee;
                    
                    return (
                      <TableRow key={order.id}>
                        <TableCell>{order.id.substring(0, 8)}...</TableCell>
                        <TableCell>{formatDate(order.createdAt)}</TableCell>
                        <TableCell>{order.customerName || 'Anonymous'}</TableCell>
                        <TableCell>{artistItems.reduce((sum, item) => sum + item.quantity, 0)}</TableCell>
                        <TableCell align="right">{formatCurrency(orderTotal)}</TableCell>
                        <TableCell align="right">{formatCurrency(artistEarnings)}</TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No orders found for the selected time period.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        )}
      </DashboardContainer>
    </Container>
  );
};

export default ArtistEarningsDashboard;
